import { chatService } from '@/services/chatService';
import { getLastQuickAnswerContext, useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { ChatMessage, ChatRequest, ChatResponse } from '@/types';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validateResponse = (response: any) => {
  const requiredFields = ['messageId', 'threadId', 'output'];

  let result = true;

  requiredFields.forEach(field => {
    if (!response[field]) {
      result = false;
    }
  });

  return result;
};

export function useChatApi() {
  const {
    messages,
    addMessage,
    addQuickAnswer,
    chatSession,
    setLoading,
    setError,
    setShowFeedbackAlert,
    showNegativeFeedbackMessageId,
    setShowNegativeFeedbackMessageId,
  } = useChatStore();

  const [inputValue, setInputValue] = useState('');

  // Mutation for sending a message
  const mutation = useMutation({
    mutationFn: async ({
      content,
      options = { addMessageToChatHistory: true },
    }: {
      content: string;
      options?: { addMessageToChatHistory?: boolean };
    }) => {
      const userMessageId = uuidv4();

      if (!chatSession.threadId) {
        throw new Error('Thread ID is not set');
      }

      // Create and add user message
      const userMessage: ChatMessage = {
        id: userMessageId,
        output: [
          {
            role: 'user',
            type: 'text',
            content,
          },
        ],
        messageId: userMessageId,
        threadId: chatSession.threadId,
        timestamp: new Date().toISOString(),
      };

      if (options?.addMessageToChatHistory) {
        addMessage(userMessage);
      }

      // Update loading state
      setLoading(true);

      try {
        // Get seen contexts from store
        const seenContexts = Array.from(useChatStore.getState().seenContexts.entries()).map(
          ([selector, description]) => ({ selector, description })
        );

        const requestContext: ChatRequest['context'] = {
          seenContent: seenContexts,
        };

        // Get quick answer context if exists
        const quickAnswerContext = getLastQuickAnswerContext();

        if (quickAnswerContext) {
          requestContext.quickQuestion = quickAnswerContext.question;
          requestContext.quickAnswer = quickAnswerContext.answer;
        }

        // Send request to API with new structure
        const language = useConfigStore.getState().config.language || 'de';

        const response = (await chatService.sendMessage({
          chatInput: content,
          sessionId: chatSession.threadId,
          action: 'sendMessage',
          language,
          context: requestContext,
        })) as ChatResponse;

        if (!validateResponse(response)) {
          addMessage({
            id: uuidv4(),
            output: [
              {
                role: 'assistant',
                type: 'text',
                content: 'Something went wrong. Try again later.',
              },
            ],
            messageId: uuidv4(),
            threadId: chatSession.threadId,
            timestamp: new Date().toISOString(),
          });
          throw new Error('Invalid response');
        }

        // Save threadId from response
        // if (response.threadId && chatSession.threadId !== response.threadId) {
        //   setThreadId(response.threadId);
        // }

        // Create and add assistant message
        const assistantMessage: ChatMessage = {
          id: response.messageId,
          output: response.output,
          messageId: response.messageId,
          threadId: response.threadId,
          suggestions: response.suggestions,
          resumeUrl: response.resumeUrl,
          timestamp: new Date().toISOString(),
        };

        addMessage(assistantMessage);

        return response;
      } catch (error) {
        // Handle error
        setError(error instanceof Error ? error : new Error('Failed to send message'));
        throw error;
      } finally {
        // Reset loading state
        setLoading(false);
      }
    },
  });

  // Mutation for sending feedback
  const sendFeedbackMutation = useMutation({
    mutationFn: async ({
      messageId,
      content,
      type,
    }: {
      messageId: string;
      content: string;
      type: 'positive' | 'negative';
    }) => {
      console.log('messageId', messageId);
      console.log('messages', useChatStore.getState().messages);

      const webhookUrl = useChatStore
        .getState()
        .messages.find(message => message.messageId === messageId)?.resumeUrl;

      if (!webhookUrl) {
        throw new Error('Webhook URL is not set in the message');
      }

      await chatService.sendFeedback(
        {
          messageId,
          content,
          type,
        },
        webhookUrl
      );
    },
  });

  // Send feedback handler
  const sendFeedback = useCallback(
    async ({
      messageId,
      content,
      type,
    }: {
      messageId: string;
      content: string;
      type: 'positive' | 'negative';
    }) => {
      setShowFeedbackAlert(true);
      await sendFeedbackMutation.mutateAsync({
        messageId,
        content,
        type,
      });
    },
    [sendFeedbackMutation, setShowFeedbackAlert]
  );

  // Send message handler
  const sendMessage = useCallback(async () => {
    if (!inputValue.trim()) return;

    const message = inputValue.trim();
    setInputValue('');

    try {
      if (showNegativeFeedbackMessageId) {
        sendFeedback({
          messageId: showNegativeFeedbackMessageId,
          content: message,
          type: 'negative',
        });
        setShowNegativeFeedbackMessageId(null);
      } else {
        await mutation.mutateAsync({
          content: message,
        });
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }, [
    inputValue,
    mutation,
    showNegativeFeedbackMessageId,
    sendFeedback,
    setShowNegativeFeedbackMessageId,
  ]);

  // Input change handler
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  }, []);

  // Send suggestion handler
  const sendSuggestion = useCallback(
    async (value: string) => {
      try {
        await mutation.mutateAsync({
          content: value,
        });
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    },
    [mutation]
  );

  // Send quick answer handler
  const sendQuickAnswer = useCallback(
    (question: string, answer: string) => {
      addQuickAnswer(question, answer);
    },
    [addQuickAnswer]
  );

  // Enter key handler (send message without Shift)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  return {
    sendMessageMutation: mutation,
    inputValue,
    handleInputChange,
    handleKeyDown,
    sendMessage,
    isLoading: mutation.isPending || chatSession.isLoading,
    error: mutation.error || chatSession.error,
    messages,
    resetChat: useChatStore().resetSession,
    sendSuggestion,
    sendQuickAnswer,
    sendFeedback,
  };
}
